/**
 * ProviderOSOnboard — provider record onboarding + credentialing-lite +
 * availability toggle. Single page, gated by provider_os.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, CheckCircle2, AlertTriangle, ShieldCheck, Plus, Loader2, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const LICENSE_TYPES = ['HHA','HOSPICE','SNF','AGENCY','RN','LPN','PT','OT','ST','CNA'];

const StatusBadge = ({ status }) => {
  const map = {
    draft:     { tone: 'bg-slate-100 text-slate-700',     label: 'Draft' },
    in_review: { tone: 'bg-amber-100 text-amber-800',     label: 'In review' },
    active:    { tone: 'bg-emerald-100 text-emerald-800', label: 'Active' },
    expired:   { tone: 'bg-red-100 text-red-800',         label: 'Expired' },
    suspended: { tone: 'bg-red-100 text-red-800',         label: 'Suspended' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status };
  return (
    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
};

export default function ProviderOSOnboard() {
  return (
    <ProviderOSGate>
      <OnboardInner />
    </ProviderOSGate>
  );
}

function OnboardInner() {
  const { token } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalName, setLegalName] = useState('');
  const [npi, setNpi] = useState('');
  const [taxLast4, setTaxLast4] = useState('');
  const [licenses, setLicenses] = useState([{ state: 'TX', license_no: '', license_type: 'HHA', expires_at: '' }]);
  const [acceptingNew, setAcceptingNew] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caqhId, setCaqhId] = useState('');
  const [bgRef, setBgRef] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/providers/me`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        setProvider(res.data);
        setLegalName(res.data.legal_name || '');
        setNpi(res.data.npi || '');
        setTaxLast4(res.data.tax_id_last4 || '');
        setLicenses(res.data.licenses?.length ? res.data.licenses :
                      [{ state: 'TX', license_no: '', license_type: 'HHA', expires_at: '' }]);
        setAcceptingNew(Boolean(res.data.availability?.accepting_new));
      }
    } catch (e) {
      // 402 means no entitlement, gate handles it
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        legal_name: legalName || null,
        npi: npi || null,
        tax_id_last4: taxLast4 || null,
        licenses: licenses.filter((l) => l.license_no.trim()),
        availability: { accepting_new: acceptingNew, service_areas: [] },
      };
      const res = await axios.post(`${API}/providers`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      setProvider(res.data);
      toast.success('Provider record saved');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const submitAttest = async () => {
    if (!provider) {
      toast.error('Save the provider record first');
      return;
    }
    if (!caqhId.trim() && !bgRef.trim()) {
      toast.error('Provide at least one credential reference');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/providers/${provider.id}/credentialing/attest`,
        { caqh_id: caqhId.trim() || null, bg_check_ref: bgRef.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setProvider(res.data);
      toast.success(`Credentialing → ${res.data.credentialing_status}`);
      setCaqhId('');
      setBgRef('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Attestation failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading provider record…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="provider-os-onboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Provider record</h1>
          <p className="text-sm text-slate-500 mt-1">
            Onboard your org — license(s), credentialing, capacity, availability.
          </p>
        </div>
        <Link to="/provider-os" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
          Dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {provider && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4"
              data-testid="provider-status-card">
          <Building2 className="w-8 h-8 text-slate-400" />
          <div className="flex-1">
            <div className="text-sm text-slate-500">Provider</div>
            <div className="text-lg font-semibold text-slate-900">{provider.legal_name}</div>
            <div className="text-xs text-slate-400 font-mono mt-1">{provider.id}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={provider.credentialing_status} />
            <span className={`text-xs ${provider.availability?.accepting_new ? 'text-emerald-700' : 'text-slate-500'}`}>
              {provider.availability?.accepting_new ? 'Accepting new placements' : 'Paused'}
            </span>
          </div>
        </div>
      )}

      {/* Org profile */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
        <h2 className="text-lg font-semibold text-slate-900">Org profile</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="legal-name">Legal name</Label>
            <Input id="legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)}
                    data-testid="onboard-legal-name" />
          </div>
          <div>
            <Label htmlFor="npi">NPI (10-digit)</Label>
            <Input id="npi" value={npi} onChange={(e) => setNpi(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="1234567890" data-testid="onboard-npi" />
          </div>
          <div>
            <Label htmlFor="tax-last4">Tax ID last 4</Label>
            <Input id="tax-last4" value={taxLast4} onChange={(e) => setTaxLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="1234" data-testid="onboard-tax-last4" />
          </div>
          <div className="flex items-end gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={acceptingNew}
                      onChange={(e) => setAcceptingNew(e.target.checked)}
                      data-testid="onboard-accepting-new" />
              Accepting new placements
            </label>
          </div>
        </div>
      </div>

      {/* Licenses */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Licenses</h2>
          <button
            onClick={() => setLicenses([...licenses, { state: 'TX', license_no: '', license_type: 'HHA', expires_at: '' }])}
            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
            data-testid="add-license-btn"
          >
            <Plus className="w-4 h-4" /> Add license
          </button>
        </div>
        {licenses.map((lic, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
                data-testid={`license-row-${i}`}>
            <div>
              <Label>State</Label>
              <Select value={lic.state} onValueChange={(v) => {
                const copy = [...licenses]; copy[i].state = v; setLicenses(copy);
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={lic.license_type} onValueChange={(v) => {
                const copy = [...licenses]; copy[i].license_type = v; setLicenses(copy);
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LICENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>License #</Label>
              <Input value={lic.license_no}
                      onChange={(e) => { const copy = [...licenses]; copy[i].license_no = e.target.value; setLicenses(copy); }}
                      placeholder="LIC-001" />
            </div>
            <div>
              <Label>Expires</Label>
              <Input type="date" value={lic.expires_at || ''}
                      onChange={(e) => { const copy = [...licenses]; copy[i].expires_at = e.target.value; setLicenses(copy); }} />
            </div>
          </div>
        ))}
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
                className="px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                data-testid="onboard-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          Save provider record
        </button>
      </div>

      {/* Credentialing-lite */}
      {provider && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6 space-y-4"
              data-testid="credentialing-lite">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <h2 className="text-lg font-semibold text-emerald-900">Credentialing-lite</h2>
          </div>
          <p className="text-sm text-emerald-900/80">
            Attest CAQH + background check. Status auto-rolls up to <span className="font-mono">in_review</span> on first signal, <span className="font-mono">active</span> when all signals are in.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="caqh">CAQH ID</Label>
              <Input id="caqh" value={caqhId} onChange={(e) => setCaqhId(e.target.value)}
                      placeholder="CAQH-XXXXXXX" data-testid="caqh-input" />
            </div>
            <div>
              <Label htmlFor="bg">Background-check ref</Label>
              <Input id="bg" value={bgRef} onChange={(e) => setBgRef(e.target.value)}
                      placeholder="CHK-XXXXX" data-testid="bg-input" />
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={submitAttest}
                    className="px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
                    data-testid="attest-submit">
              <CheckCircle2 className="w-4 h-4 inline mr-1" /> Attest
            </button>
          </div>
          <div className="text-xs text-emerald-900/60 mt-2">
            Progress:{' '}
            <code>{JSON.stringify(provider.credentialing_progress || {})}</code>
          </div>
        </div>
      )}
    </div>
  );
}
